/**
 * Per-tab registry of doc update logs (browser).
 *
 * Mirrors `websocket-server/src/yjs/docUpdateLogRegistry.js` exactly. See
 * that file for the full architectural rationale.
 *
 * Why a registry on the client
 * ────────────────────────────
 * The same Y.Doc is referenced by multiple call sites (the WS provider for
 * sync, the IDB persistence for local-first, the React store for rendering,
 * stale-check logic). Each of those sites that wants to encode the doc's
 * state benefits from sharing one log per doc — the seed cost is paid
 * once, and subsequent compactions use the consolidated state.
 *
 * Reference: `/workspace/.cursor/plans/websocket_robustness_refactor_b886ced2.plan.md`
 */

import * as Y from 'yjs';
import { createDocUpdateLog, type DocUpdateLog, type CreateDocUpdateLogOptions } from './docUpdateLog';

export interface DocUpdateLogRegistry {
	getOrCreate(doc: Y.Doc, options?: CreateDocUpdateLogOptions): DocUpdateLog;
	has(doc: Y.Doc): boolean;
	stats(): { count: number; totalBytes: number };
	clear(): void;
}

export interface DocUpdateLogRegistryOptions {
	factory?: (doc: Y.Doc, opts?: CreateDocUpdateLogOptions) => DocUpdateLog;
	logger?: { warn?: (...args: unknown[]) => void };
}

export function createDocUpdateLogRegistry(
	options: DocUpdateLogRegistryOptions = {},
): DocUpdateLogRegistry {
	const {
		factory = createDocUpdateLog,
		logger = console,
	} = options;

	const docToLog = new WeakMap<Y.Doc, DocUpdateLog>();
	const liveLogs = new Set<DocUpdateLog>();

	function getOrCreate(doc: Y.Doc, opts?: CreateDocUpdateLogOptions): DocUpdateLog {
		if (!doc || typeof doc.on !== 'function') {
			throw new TypeError('docUpdateLogRegistry: doc must be a Y.Doc');
		}
		const existing = docToLog.get(doc);
		if (existing && !existing.isDestroyed()) return existing;

		const log = factory(doc, opts);
		docToLog.set(doc, log);
		liveLogs.add(log);

		const onDocDestroy = () => {
			try { log.destroy(); } catch { /* best-effort */ }
			liveLogs.delete(log);
			docToLog.delete(doc);
		};
		try {
			doc.once('destroy', onDocDestroy);
		} catch (err) {
			logger.warn?.('[docUpdateLogRegistry] doc.once unavailable:', (err as Error).message);
			doc.on('destroy', onDocDestroy);
		}

		return log;
	}

	function has(doc: Y.Doc): boolean {
		const log = docToLog.get(doc);
		return Boolean(log && !log.isDestroyed());
	}

	function stats() {
		let totalBytes = 0;
		let count = 0;
		for (const log of liveLogs) {
			if (log.isDestroyed()) continue;
			totalBytes += log.bytes();
			count++;
		}
		return { count, totalBytes };
	}

	function clear() {
		for (const log of liveLogs) {
			try { log.destroy(); } catch { /* best-effort */ }
		}
		liveLogs.clear();
	}

	return { getOrCreate, has, stats, clear };
}

// ── Module-level singleton ──────────────────────────────────────────────────
// Most callers want a single shared registry per tab — that way the same
// doc gets the same log no matter which call site asks for it.

let _singleton: DocUpdateLogRegistry | null = null;

export function getDocUpdateLogRegistry(): DocUpdateLogRegistry {
	if (!_singleton) _singleton = createDocUpdateLogRegistry();
	return _singleton;
}

export function _resetDocUpdateLogRegistrySingletonForTests(): void {
	if (_singleton) _singleton.clear();
	_singleton = null;
}
